import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Bell, AlertTriangle, CheckCircle, Copy } from 'lucide-react';
import { requestForToken, onMessageListener, logErrorToAnalytics } from './firebase';

const NotificationWidget = ({ onTokenReceived }) => {
    const [notification, setNotification] = useState({ title: '', body: '' });
    const [token, setToken] = useState('');
    const [isTokenLoading, setIsTokenLoading] = useState(false);
    const [error, setError] = useState('');
    const [simulatedCrash, setSimulatedCrash] = useState(false);

    useEffect(() => {
        if (notification?.title) {
            // Auto-clear notification after 5 seconds
            const timer = setTimeout(() => {
                setNotification({ title: '', body: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    useEffect(() => {
        // Listen for foreground messages
        onMessageListener()
            .then((payload) => {
                setNotification({
                    title: payload?.notification?.title,
                    body: payload?.notification?.body,
                });
            })
            .catch((err) => console.log('failed: ', err));
    }, []);

    const handleRequestPermission = async () => {
        setIsTokenLoading(true);
        setError('');
        const fetchedToken = await requestForToken();
        setIsTokenLoading(false);
        if (fetchedToken) {
            setToken(fetchedToken);
            if (onTokenReceived) {
                onTokenReceived(fetchedToken);
            }
        } else {
            setError('Failed to get token. Check console and ensure firebaseConfig is set.');
        }
    };

    const handleSimulateCrash = () => {
        try {
            throw new Error("Simulated Crash for Crashlytics Testing");
        } catch (err) {
            console.error("Caught Simulated Crash:", err);
            logErrorToAnalytics(err, { fatal: true, component: 'NotificationWidget' });
            setSimulatedCrash(true);
            setTimeout(() => setSimulatedCrash(false), 3000);
        }
    };

    const handleTestLocalNotification = () => {
        setNotification({
            title: "Test Notification",
            body: "This is a local test notification to verify the UI."
        });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(token);
        alert('Token copied to clipboard!');
    };

    return (
        <div className="w-full max-w-4xl mx-auto p-4 font-sans">

            {/* Foreground Notification Toast */}
            {notification.title && (
                <div className="fixed top-5 right-5 z-50 bg-white border-l-4 border-green-500 shadow-xl rounded-lg p-4 max-w-sm animate-bounce-in">
                    <div className="flex items-start gap-3">
                        <Bell className="text-green-500 mt-1" size={20} />
                        <div>
                            <h4 className="font-bold text-slate-800">{notification.title}</h4>
                            <p className="text-sm text-slate-600">{notification.body}</p>
                        </div>
                        <button onClick={() => setNotification({ title: '', body: '' })} className="text-slate-400 hover:text-slate-600">
                            &times;
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Bell className="text-indigo-500" />
                        Firebase Notifications & Crashlytics
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Test push notifications and error logging.
                    </p>
                </div>

                <div className="p-8 space-y-8">

                    {/* Notification Section */}
                    <div className="flex flex-col md:flex-row gap-8">
                        <div className="flex-1">
                            <h3 className="font-semibold text-slate-700 mb-4">Push Notifications</h3>

                            {!token ? (
                                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 text-center">
                                    <Bell size={32} className="mx-auto text-indigo-400 mb-3" />
                                    <p className="text-slate-600 mb-4 text-sm">
                                        Enable notifications to receive updates.
                                    </p>
                                    <button
                                        onClick={handleRequestPermission}
                                        disabled={isTokenLoading}
                                        className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                    >
                                        {isTokenLoading ? 'Requesting...' : 'Enable Notifications'}
                                    </button>
                                    {error && <p className="text-red-500 text-xs mt-3">{error}</p>}


                                    {/* Test Local Notification Button */}
                                    <div className="mt-4">
                                        <button
                                            onClick={handleTestLocalNotification}
                                            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors w-full"
                                        >
                                            Test Local Notification (UI Only)
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-green-50 border border-green-100 rounded-xl p-6">
                                    <div className="flex items-center gap-2 text-green-700 font-bold mb-2">
                                        <CheckCircle size={20} />
                                        Notifications Enabled
                                    </div>
                                    <p className="text-xs text-slate-500 mb-2">Device Token (FCM):</p>
                                    <div className="bg-white border border-slate-200 rounded p-2 flex items-center justify-between gap-2">
                                        <code className="text-xs text-slate-600 truncate flex-1 block">
                                            {token}
                                        </code>
                                        <button onClick={copyToClipboard} className="text-slate-400 hover:text-indigo-600">
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 italic">
                                        Use this token in Firebase Console to send a test message.
                                    </p>
                                </div>
                            )}


                        </div>

                        {/* Crashlytics Section */}
                        <div className="flex-1">
                            <h3 className="font-semibold text-slate-700 mb-4">Crashlytics Simulation</h3>
                            <div className="bg-red-50 border border-red-100 rounded-xl p-6">
                                <AlertTriangle size={32} className="text-red-400 mb-3" />
                                <p className="text-slate-600 mb-4 text-sm">
                                    Simulate a crash to test error reporting.
                                </p>
                                <button
                                    onClick={handleSimulateCrash}
                                    className="px-5 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors w-full"
                                >
                                    Test Crash
                                </button>
                                {simulatedCrash && (
                                    <p className="text-xs text-red-600 mt-3 font-medium animate-pulse">
                                        Error logged to console! (Simulated)
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Config Reminder */}
                        {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 flex items-start gap-3">
                            <AlertTriangle className="shrink-0 mt-0.5" size={16} />
                            <div>
                                <strong>Configuration Required:</strong> To make this work, you must update
                                <code className="bg-yellow-100 px-1 rounded mx-1">src/firebase.js</code>
                                and
                                <code className="bg-yellow-100 px-1 rounded mx-1">public/firebase-messaging-sw.js</code>
                                with your actual Firebase Project keys.
                            </div>
                        </div> */}

                </div>
            </div>
        </div>
    );
};

NotificationWidget.propTypes = {
    onTokenReceived: PropTypes.func,
};

export default NotificationWidget;
